import { useState } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { domainService, type Domain, type DomainCreateData } from '@/services/domain-service';
import {
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Checkbox,
  Label,
  toast,
} from 'prosci-ui';
import { Plus, Edit, Trash2, MoreHorizontal, FileText } from '@/lib/icons';

type DomainForm = {
  slug: string;
  name: string;
  description: string;
  system_prompt_guidance: string;
  is_active: boolean;
  sort_order: number;
};

const emptyForm: DomainForm = {
  slug: '',
  name: '',
  description: '',
  system_prompt_guidance: '',
  is_active: true,
  sort_order: 0,
};

export function DomainsTab() {
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<DomainForm>(emptyForm);
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainService.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: DomainCreateData) => domainService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast.success('Domain created');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create domain'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DomainCreateData }) =>
      domainService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast.success('Domain updated');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update domain'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => domainService.destroy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast.success('Domain deleted');
      setDomainToDelete(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete domain'),
  });

  const domains: Domain[] =
    extractAttributesList(data);

  const openCreate = () => {
    setEditingDomain(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (domain: Domain) => {
    setEditingDomain(domain);
    setForm({
      slug: domain.slug,
      name: domain.name,
      description: domain.description || '',
      system_prompt_guidance: domain.system_prompt_guidance || '',
      is_active: domain.is_active,
      sort_order: domain.sort_order,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDomain(null);
    setForm(emptyForm);
  };

  const handleSave = () => {
    if (editingDomain) {
      updateMutation.mutate({ id: editingDomain.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Domain
        </Button>
      </div>

      {isLoading ? (
        <Empty><Spinner /></Empty>
      ) : domains.length === 0 ? (
        <Empty>
          <EmptyIcon><FileText className="h-12 w-12" /></EmptyIcon>
          <EmptyTitle>No domains</EmptyTitle>
          <EmptyDescription>Create domains to categorize Kaiya's knowledge areas.</EmptyDescription>
          <Button onClick={openCreate} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            New Domain
          </Button>
        </Empty>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell className="font-mono text-sm">{domain.slug}</TableCell>
                    <TableCell className="font-medium">{domain.name}</TableCell>
                    <TableCell>
                      <Badge variant={domain.is_active ? 'default' : 'secondary'}>
                        {domain.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{domain.sort_order}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(domain)}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDomainToDelete(domain)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={closeForm}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingDomain ? 'Edit Domain' : 'New Domain'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Slug</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="adkar"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Name</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="ADKAR Model"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">System Prompt Guidance</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm font-mono"
                rows={4}
                placeholder="Guidance injected into prompts for this domain..."
                value={form.system_prompt_guidance}
                onChange={(e) => setForm({ ...form, system_prompt_guidance: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Sort Order</label>
                <input
                  type="number"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-end">
                <Label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: !!checked })}
                  />
                  Active
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!domainToDelete} onOpenChange={() => setDomainToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Domain</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "{domainToDelete?.name}"?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDomainToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => domainToDelete && deleteMutation.mutate(domainToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
